<template>
	<div>
		<div class="d-flex align-center amelipro-checkbox">
			<label class="mr-0 d-inline-flex flex-row align-center text-body-1 amelipro-checkbox__label">
				<input
					v-model="modelValue"
					:aria-describedby="checkErrorCondition ? `${id}-error ` + checkbox.description : checkbox.description"
					class="mr-2"
					:disabled="disabled"
					:name="`${id}-name`"
					type="checkbox"
					:value="checkbox.value"
					@input="emitChangeEvent"
				>
				<span :class="labelColorClasses">
					<slot name="label">
						{{ checkbox.label }}
					</slot>

					<span
						v-if="ariaRequired"
						aria-hidden="true"
						class="d-inline-flex"
					>
						&nbsp;*
					</span>
					<span
						v-if="ariaRequired"
						class="d-sr-only"
					>
						&nbsp;Champ obligatoire
					</span>
				</span>
			</label>

			<slot name="append" />
		</div>

		<AmeliproMessage
			v-if="checkErrorCondition"
			:id="`${id}-error`"
			no-icon
			text
			type="error"
		>
			<p class="mb-0">
				{{ error }}
			</p>
		</AmeliproMessage>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed, ref, watch } from 'vue';
	import { AmeliproCheckboxItem } from './types';
	import AmeliproMessage from '../AmeliproMessage/AmeliproMessage.vue';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		checkbox: {
			type: Object as PropType<AmeliproCheckboxItem>,
			required: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		errorDefault: {
			type: Boolean,
			default: false,
		},
		id: {
			type: String,
			required: true,
		},
		requiredErrorMessage: {
			type: String,
			default: 'Sélection obligatoire',
		},
		value: {
			type: Boolean,
			default: false,
		},
	});

	const isAlreadyCheckedOnce = ref(props.errorDefault);

	const labelColorClasses = computed<string>(() => {
		if (props.disabled) {
			return 'ap-grey--text';
		}

		if (error.value !== '') {
			return 'ap-error--text';
		}

		return 'ap-grey--text text--darken-1';
	});

	const modelValue = computed({
		get: () => props.value,
		set: (value: boolean) => {
			isAlreadyCheckedOnce.value = true;
			emit('input', value, props.checkbox.value);
		},
	});

	const emit = defineEmits(['input']);
	const emitChangeEvent = (): void => {
		modelValue.value = !modelValue.value;
	};

	const error = computed<string>(() => (isAlreadyCheckedOnce.value === true && props.ariaRequired === true && modelValue.value === false ? props.requiredErrorMessage : ''));
	const checkErrorCondition = computed<boolean>(() => (isAlreadyCheckedOnce.value === true && props.ariaRequired === true && modelValue.value === false));

	watch(() => props.errorDefault, (newErrorDefaultValue: boolean) => {
		isAlreadyCheckedOnce.value = newErrorDefaultValue;
	});
</script>

<style lang="scss" scoped>
input {
	position: absolute;
	width: 0;
	height: 0;
	opacity: 0;

	&:focus {
		&+span {
			outline: 1px dotted $ap-grey-darken1;
		}
	}

	&+span {
		position: relative;
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
		padding-left: 2rem;
		cursor: pointer;

		&::before {
			position: absolute;
			top: 0.125rem;
			left: 0;
			width: 1.5rem;
			height: 1.5rem;
			border-radius: 0.4rem;
			border: 3px solid $ap-blue-darken1;
			background-color: transparent;
			content: "";
		}
	}

	&:checked {

		&+span {

			&::before {
				background-color: $ap-blue-darken1;
			}

			&::after {
				position: absolute;
				top: 0.525rem;
				left: 0.3rem;
				width: 0.9rem;
				height: 0.5rem;
				border-radius: 2px;
				border-bottom: 3px solid $ap-white;
				border-left: 3px solid $ap-white;
				transform: rotate(-50deg);
				content: "";
			}
		}
	}

	&:disabled {

		&+span {
			cursor: default;

			&::before {
				opacity: 0.6;
			}
		}
	}
}
</style>
